import React from "react";
import { Icon } from "@fluentui/react";
import { EndpointData } from "../hooks/useCurrentPageContext";
import { GroupedChatContext } from "../hooks/useChatContextTracker";

interface ChatInputProps {
    value: string;
    onChange: (value: string) => void;
    onSend: () => void;
    onStop?: () => void;
    loading?: boolean;
    disabled?: boolean;
    placeholder?: string;
    style?: "welcome" | "bottom";
    availableEndpoints?: EndpointData[];
    attachedEndpoints?: EndpointData[];
    onAttachEndpoint?: (endpoint: EndpointData) => void;
    onRemoveEndpoint?: (endpointKey: string) => void;
    trackedContext?: GroupedChatContext;
}

const ChatInput: React.FC<ChatInputProps> = ({
    value,
    onChange,
    onSend,
    onStop,
    loading = false,
    disabled = false,
    placeholder = "Type a message...",
    style = "welcome",
    availableEndpoints = [],
    attachedEndpoints = [],
    onAttachEndpoint,
    onRemoveEndpoint,
    trackedContext
}) => {
    const isSendDisabled = value.trim() === "" && !loading;

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            if (loading && onStop) {
                onStop();
            } else {
                onSend();
            }
        }
    };

    const handleButtonClick = () => {
        if (loading && onStop) {
            onStop();
        } else {
            onSend();
        }
    };

    const containerStyle = style === "welcome" ? {
        display: "flex",
        alignItems: "center",
        borderRadius: 24,
        padding: "6px 14px",
        background: "#fff",
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        border: "1px solid #e1e4e8",
        height: 40,
        width: "100%",
    } : {
        display: "flex",
        alignItems: "center",
        borderRadius: 24,
        padding: "6px 12px",
        background: "#fff",
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        border: "1px solid #e1e4e8",
        flex: 1
    };

    const buttonMargin = style === "welcome" ? 8 : 4;

    // Filter available endpoints to exclude already attached ones
    const selectableEndpoints = availableEndpoints.filter(endpoint =>
        !attachedEndpoints.some(attached => attached.fullEndpoint === endpoint.fullEndpoint)
    );

    const handleEndpointSelect = (endpoint: EndpointData) => {
        if (onAttachEndpoint) {
            onAttachEndpoint(endpoint);
        }
    };

    const handleEndpointRemove = (endpointKey: string) => {
        if (onRemoveEndpoint) {
            onRemoveEndpoint(endpointKey);
        }
    };

    const wrapperStyle = style === "welcome" ? {
        width: "90%",
        display: "flex",
        flexDirection: "column" as const,
        gap: 8
    } : {
        flex: 1,
        display: "flex",
        flexDirection: "column" as const,
        gap: 8
    };

    return (
        <div style={wrapperStyle}>
            {/* Available endpoints row */}
            {selectableEndpoints.length > 0 && (
                <div style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 6,
                    padding: "8px 0"
                }}>
                    <span style={{
                        fontSize: 12,
                        color: "#605e5c",
                        fontWeight: 500,
                        alignSelf: "center"
                    }}>
                        Available data:
                    </span>
                    {selectableEndpoints.map((endpoint) => {
                        const isAlreadyInChat = trackedContext?.uniqueEndpointsByFullEndpoint.has(endpoint.fullEndpoint) || false;
                        return (
                            <button
                                key={endpoint.fullEndpoint}
                                onClick={() => handleEndpointSelect(endpoint)}
                                style={{
                                    background: isAlreadyInChat ? "#fff3cd" : "#f3f2f1",
                                    border: isAlreadyInChat ? "1px solid #ffc107" : "1px solid #d2d0ce",
                                    borderRadius: 12,
                                    padding: "4px 8px",
                                    fontSize: 11,
                                    color: isAlreadyInChat ? "#856404" : "#323130",
                                    cursor: "pointer",
                                    transition: "all 0.2s",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 4,
                                    opacity: isAlreadyInChat ? 0.8 : 1
                                }}
                                onMouseEnter={(e) => {
                                    if (!isAlreadyInChat) {
                                        e.currentTarget.style.background = "#e1dfdd";
                                        e.currentTarget.style.borderColor = "#0078d4";
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (!isAlreadyInChat) {
                                        e.currentTarget.style.background = "#f3f2f1";
                                        e.currentTarget.style.borderColor = "#d2d0ce";
                                    }
                                }}
                                title={endpoint.dataExtractor?.description || (isAlreadyInChat ? "This endpoint is already present in the chat" : "Click to attach this endpoint")}
                            >
                                <Icon iconName={isAlreadyInChat ? "History" : "Add"} style={{ fontSize: 10 }} />
                                {endpoint.dataExtractor.label}
                                {isAlreadyInChat && (
                                    <span style={{ fontSize: 9, opacity: 0.8 }}>(in chat)</span>
                                )}
                            </button>
                        );
                    })}
                </div>
            )}

            {/* Input text box */}
            <div style={containerStyle}>
                <input
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={placeholder}
                    disabled={disabled || loading}
                    style={{
                        flex: 1,
                        border: "none",
                        outline: "none",
                        padding: "12px 14px",
                        fontSize: 14,
                        borderRadius: 22,
                        background: "transparent"
                    }}
                />
                <button
                    onClick={handleButtonClick}
                    disabled={isSendDisabled}
                    style={{
                        background: "transparent",
                        border: "none",
                        cursor: isSendDisabled ? "not-allowed" : "pointer",
                        fontSize: 18,
                        marginLeft: buttonMargin,
                        color: loading ? "#D9534F" : isSendDisabled ? "#cfcfcf" : "#0078D7",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                    }}
                >
                    <Icon iconName={loading ? "Stop" : "Send"} />
                </button>
            </div>

            {/* Attached endpoints */}
            {attachedEndpoints.length > 0 && (
                <div style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 6,
                    padding: "8px 0"
                }}>
                    <span style={{
                        fontSize: 12,
                        color: "#605e5c",
                        fontWeight: 500,
                        alignSelf: "center"
                    }}>
                        Attached:
                    </span>
                    {attachedEndpoints.map((endpoint) => (
                        <div
                            key={endpoint.fullEndpoint}
                            style={{
                                background: "#e3f2fd",
                                border: "1px solid #0078d4",
                                borderRadius: 12,
                                padding: "4px 8px",
                                fontSize: 11,
                                color: "#0078d4",
                                display: "flex",
                                alignItems: "center",
                                gap: 6
                            }}
                        >
                            <Icon iconName="Attach" style={{ fontSize: 10 }} />
                            <span title={endpoint.dataExtractor?.description || ""}>{endpoint.dataExtractor.label}</span>
                            <button
                                onClick={() => handleEndpointRemove(endpoint.fullEndpoint)}
                                style={{
                                    background: "transparent",
                                    border: "none",
                                    cursor: "pointer",
                                    color: "#0078d4",
                                    padding: 0,
                                    display: "flex",
                                    alignItems: "center",
                                    fontSize: 10
                                }}
                                title="Remove attachment"
                            >
                                <Icon iconName="Cancel" />
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ChatInput;